/* eslint-disable @typescript-eslint/no-explicit-any */
import eyeIcon from '../../../assets/svg/eye-primary.svg';
import CheckIcon from '@/assets/svg/check-stroke.svg?react';
import Datatable from '@/components/common/Datatable';
import useCustomToast from '@/components/common/Toast';
import {
  useGetAllOrder,
  useMoveStatus,
  useUploadMaintenanceImage,
} from '@/services/OrderManagement';
import {
  Box,
  Flex,
  Icon,
  Text,
  Tooltip,
  Modal,
  Image,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalHeader,
  ModalCloseButton,
  Spinner,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { convertDateFormat } from './../../../utils/dateConverter';
import StatusBadge from '@/components/common/StatusBadge';
import OrderDetails from '../OrderDetail';
import { Order, IGetAllOrdersResponse } from '@/services/OrderManagement/types';
import { Column } from 'react-table';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import CustomizedIcon from '../../../assets/svg/customizable-icon.svg?react';
import CustomizationDetailsModal from '@/components/common/CustomizationDetailsModal';
import Button from '@/components/common/Button';
import MaintenanceConfirmationModal from '@/components/Orders/MaintenanceConfirmationModal';

interface FilterState {
  start_date?: string;
  end_date?: string;
  category?: string;
  subcategory?: string;
  assign_title?: string;
  user_status?: boolean | null;
}

interface UnderMaintenanceProps {
  filters?: FilterState;
  setFilters?: React.Dispatch<React.SetStateAction<FilterState>>;
}

type OrderResponse = Order[] | IGetAllOrdersResponse | undefined;

const UnderMaintenance = ({
  filters: parentFilters,
  setFilters: setParentFilters,
}: UnderMaintenanceProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalOrderId, setConfirmModalOrderId] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isCustomizationModalOpen, setIsCustomizationModalOpen] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });

  const [query, setQuery] = useState<string>('');

  const [localFilters, setLocalFilters] = useState<FilterState>(
    parentFilters || {
      start_date: '',
      end_date: '',
      category: '',
      subcategory: '',
      assign_title: '',
      user_status: null,
    },
  );

  useEffect(() => {
    if (parentFilters) {
      setLocalFilters(parentFilters);
    }
  }, [parentFilters]);

  const effectiveFilters = parentFilters ?? localFilters;

  const handleSetFilters = (newFilters: FilterState | ((prev: FilterState) => FilterState)) => {
    setLocalFilters(newFilters);
    if (setParentFilters) {
      setParentFilters(newFilters);
    }
  };

  const { showToast } = useCustomToast();

  const {
    data: responseData,
    isLoading,
    isError,
  } = useGetAllOrder({
    status: 'Under Maintainance',
    q: query,
    page: pagination.page,
    page_size: pagination.pageSize,
    ...effectiveFilters,
  });

  const response = responseData as OrderResponse;

  const orders: Order[] = Array.isArray(response) ? response : response?.results ?? [];

  const totalCount = Array.isArray(response) ? orders.length : response?.count ?? 0;

  const totalPages = Array.isArray(response) ? 1 : response?.page_count ?? 1;

  const currentPage = Array.isArray(response) ? 1 : response?.active_page ?? pagination.page;

  useEffect(() => {
    if (responseData) {
      setPagination((prev) => ({
        ...prev,
        totalCount,
        totalPages,
        page: currentPage,
      }));
    }
  }, [responseData, totalCount, totalPages, currentPage]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSearch = (searchTerm: string) => {
    setQuery(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const applyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const resetFilters = () => {
    const resetState = {
      start_date: '',
      end_date: '',
      category: '',
      subcategory: '',
      assign_title: '',
      user_status: null,
    };
    setLocalFilters(resetState);
    if (setParentFilters) {
      setParentFilters(resetState);
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const { mutateAsync: moveStatus, isLoading: isMovingStatus } = useMoveStatus();
  const { mutateAsync: uploadImage, isLoading: isUploadingImage } = useUploadMaintenanceImage();

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const handleViewOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrderId(null);
  };

  const handleOpenConfirmModal = (orderId: string) => {
    setConfirmModalOrderId(orderId);
    setIsConfirmModalOpen(true);
  };

  const openCustomizationModal = (order: Order) => {
    setSelectedOrder(order);
    setIsCustomizationModalOpen(true);
  };

  const columns = useMemo<Column<Order>[]>(
    () => [
      {
        Header: 'Order ID',
        accessor: 'order_id',
        Cell: ({ value, row }: { value: string; row: { original: Order } }) => {
          const isCustomized = row.original.is_customized;
          return (
            <Flex align="center" gap={2}>
              <Text>{value ?? '-'}</Text>
              {isCustomized && (
                <Tooltip label="This product is customized" placement="bottom" hasArrow>
                  <Box
                    bg={'#F2F4F8'}
                    borderRadius={'3px'}
                    p={1}
                    cursor="pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      openCustomizationModal(row.original);
                    }}
                    _hover={{ bg: 'brand.primary.50' }}
                  >
                    <Icon as={CustomizedIcon} color={'brand.primary.DEFAULT'} boxSize={4} />
                  </Box>
                </Tooltip>
              )}
            </Flex>
          );
        },
      },
      {
        Header: 'Product Name',
        accessor: 'product',
        Cell: ({ value }: { value: string }) => <Text>{value ?? '-'}</Text>,
      },
      {
        Header: 'Ship To',
        accessor: (row) => row.shipping_address?.name ?? '-',
        Cell: ({ value }: { value: string }) => <Text>{value}</Text>,
      },
      {
        Header: 'Status',
        accessor: (row) => row.order_status?.status_name ?? '-',
        Cell: ({ value }: { value: string }) => <StatusBadge status={value} />,
      },
      {
        Header: 'Total Price',
        accessor: 'total_price',
        Cell: ({ value, row }: { value: string; row: { original: Order } }) => {
          const price = value ? parseFloat(value) : null;
          const currency = row.original.currency ?? 'USD';
          return (
            <Text>
              {price !== null ? `${getCurrencySymbol(currency)}${price.toFixed(2)}` : '-'}
            </Text>
          );
        },
      },
      {
        Header: 'Payment',
        accessor: (row) => row.transaction?.payment_method ?? '-',
        Cell: ({ value }: { value: string }) => <Text>{value}</Text>,
      },
      {
        Header: 'Order Placed',
        accessor: 'created_at',
        Cell: ({ value }: { value: string }) => <Text>{convertDateFormat(value)}</Text>,
      },
      {
        Header: 'Action',
        id: 'action',
        Cell: ({ row }: { row: { original: Order } }) => {
          return (
            <Flex gap={2} align="center" minWidth="80px">
              <Tooltip label="View Order" placement="bottom" hasArrow>
                <span>
                  <Image
                    src={eyeIcon}
                    boxSize={5}
                    color="brand.primary.DEFAULT"
                    onClick={() => handleViewOrder(row.original.order_id)}
                    cursor="pointer"
                  />
                </span>
              </Tooltip>

              <Tooltip label="Mark as Ready for Delivery" placement="bottom-start" hasArrow>
                {isMovingStatus || isUploadingImage ? (
                  <Spinner size="sm" color="brand.primary.DEFAULT" thickness="2px" />
                ) : (
                  <span>
                    <Icon
                      as={CheckIcon}
                      boxSize={5}
                      color="brand.primary.DEFAULT"
                      onClick={() => handleOpenConfirmModal(row.original.order_id)}
                      cursor="pointer"
                    />
                  </span>
                )}
              </Tooltip>
            </Flex>
          );
        },
      },
    ],
    [isMovingStatus, isUploadingImage],
  );

  return (
    <Box bg="background.white">
      <Box width="auto" overflow="auto">
        <Datatable<Order>
          data={orders}
          isLoading={isLoading}
          isError={isError}
          columns={columns}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            totalCount: pagination.totalCount,
            totalPages: pagination.totalPages,
          }}
          onSearch={handleSearch}
          onPageChange={handlePageChange}
          filters={effectiveFilters}
          setFilters={handleSetFilters}
          applyFilters={applyFilters}
          resetFilters={resetFilters}
          sticky={true}
        />
      </Box>

      <MaintenanceConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setConfirmModalOrderId(null);
        }}
        onConfirm={async (file, comment) => {
          if (!confirmModalOrderId) return;

          const currentOrder = orders.find((o) => o.order_id === confirmModalOrderId);
          if (!currentOrder) {
            showToast('error', 'Order not found');
            return;
          }

          const formData = new FormData();
          formData.append('image', file);
          formData.append('order_item_id', String(currentOrder.id));
          if (comment.trim()) formData.append('comment', comment.trim());

          try {
            await uploadImage(formData);

            const response = await moveStatus({
              orderId: confirmModalOrderId,
              newStatus: 'Ready for Delivery',
            });

            showToast('success', response?.message || 'Order moved to Ready for Delivery');
            setIsConfirmModalOpen(false);
            setConfirmModalOrderId(null);
          } catch (error: any) {
            const message =
              error?.response?.data?.message ||
              error?.response?.data?.detail ||
              error?.message ||
              'Failed to complete the process';
            showToast('error', message);
          }
        }}
        description={`Are you sure you want to move order ${confirmModalOrderId || ''} to Ready for Delivery?`}
        imageLabel="Upload ORDER COMPLETION IMAGE"
        targetStatus="Ready for Delivery"
        isLoading={isUploadingImage || isMovingStatus}
      />

      <Modal size="5xl" isOpen={isModalOpen} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Order Details</ModalHeader>
          <ModalBody
            maxHeight="70vh"
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'gray.300', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'gray.400' },
            }}
          >
            {selectedOrderId && <OrderDetails id={selectedOrderId} />}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="brand.primary" onClick={closeModal}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CustomizationDetailsModal
        isOpen={isCustomizationModalOpen}
        onClose={() => setIsCustomizationModalOpen(false)}
        order={selectedOrder ?? undefined}
      />
    </Box>
  );
};

export default UnderMaintenance;
